/**
 * Storefront API client — a lean fetch adapter for the public resale
 * storefront, mirroring the portal SPA's `portal-erp.ts` (the sanctioned
 * precedent). It hits the same-origin `/api/store/*` endpoints (Caddy proxies
 * `/api` to the Core API un-stripped, so the SPA stays single-origin — no
 * CORS). Two classes of endpoint, one client:
 *
 *   * ANONYMOUS — the public catalog browse + buyer self-registration/verify.
 *     No token, no cookie (the backend authenticates nobody on these routes and
 *     emits fixed uniform response shapes).
 *   * BEARER — the buyer's own-orders read + checkout, authenticated with the
 *     `pipe-storefront` PKCE access token (`Surface::Storefront`). Bearer, NOT
 *     cookie: no `credentials: 'include'` anywhere (mirrors the portal).
 *
 * This surface only ever consumes the anonymous/own-account endpoints — NEVER
 * a staff read (`/api/entities`, `/api/records`, …). This is NOT a
 * `@toeverything/infra` DI service — the storefront is a separate, lean origin
 * and does not pull `@affine/core`.
 */

/**
 * The Storefront redact projection: the external serializer emits a positive
 * allowlist only — no facility_id / stage / allowed_transitions / internal
 * provenance ever reaches this surface.
 */
export interface StorefrontRecord {
  id: string;
  type: string;
  title: string | null;
  title_redacted: boolean;
  data: Record<string, unknown>;
  account_id: string | null;
  created_at: string;
  updated_at: string;
}

/** The buyer self-registration body (`email` required; the rest optional). */
export interface RegisterBody {
  email: string;
  name?: string;
  company?: string;
  locale?: string;
}

/**
 * The uniform registration outcome. The backend deliberately collapses
 * new/duplicate/blocked registrations into ONE `202 pending` shape (no
 * membership oracle); `disabled` is the config-off 404 and `rate_limited` the
 * uniform 429. Anything else is `unavailable`.
 */
export type RegisterOutcome =
  | 'pending'
  | 'disabled'
  | 'rate_limited'
  | 'unavailable';

/** The verify outcome: verified, an invalid/expired token, or a server error. */
export type VerifyOutcome =
  | 'verified'
  | 'invalid'
  | 'rate_limited'
  | 'unavailable';

/** The buyer-safe checkout receipt (exactly what the backend stores for replay). */
export interface CheckoutReceipt {
  order_id: string;
  order_no: string;
  state: string | null;
  payment_status: string;
  currency: string | null;
  total_amount: number;
  listing_ids: string[];
}

export class StorefrontApiService {
  constructor(private readonly getToken: () => string | null) {}

  /** Bearer-header construction shared by every authed call (mirrors portal). */
  private authHeaders(): Record<string, string> {
    const token = this.getToken();
    return token ? { authorization: `Bearer ${token}` } : {};
  }

  private async api<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(`/api${path}`, {
      headers: {
        accept: 'application/json',
        ...this.authHeaders(),
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
      },
      ...init,
    });
    if (!res.ok) {
      throw new Error(
        `storefront api ${init?.method ?? 'GET'} ${path}: ${res.status}`
      );
    }
    return (await res.json()) as T;
  }

  // ---- anonymous: public catalog ------------------------------------------

  /** The bounded public catalog page (active listings only, server-clamped). */
  browseListings(limit?: number, offset?: number): Promise<StorefrontRecord[]> {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.set('limit', String(limit));
    }
    if (offset !== undefined) {
      params.set('offset', String(offset));
    }
    const qs = params.size > 0 ? `?${params.toString()}` : '';
    return this.api<StorefrontRecord[]>(`/store/listings${qs}`);
  }

  /** One active listing by id (404 for draft/sold/missing — surfaces as throw). */
  browseListing(id: string): Promise<StorefrontRecord> {
    return this.api<StorefrontRecord>(`/store/listings/${id}`);
  }

  // ---- anonymous: buyer self-registration ---------------------------------

  /**
   * POST /api/store/register — uniform by design: a 202 is ALWAYS `pending`
   * (whether new, duplicate, or blocked — no oracle). Classified, never thrown:
   * every outcome is a state the register screen renders.
   */
  async register(body: RegisterBody): Promise<RegisterOutcome> {
    return this.registrationPost('/store/register', body);
  }

  /** POST /api/store/resend-verification — same uniform classification. */
  async resendVerification(email: string): Promise<RegisterOutcome> {
    return this.registrationPost('/store/resend-verification', { email });
  }

  private async registrationPost(
    path: string,
    body: RegisterBody
  ): Promise<RegisterOutcome> {
    const res = await fetch(`/api${path}`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (res.status === 202) {
      return 'pending';
    }
    if (res.status === 404) {
      return 'disabled'; // registration config off — indistinguishable from unknown route, by design
    }
    if (res.status === 429) {
      return 'rate_limited';
    }
    return 'unavailable';
  }

  /**
   * POST /api/store/verify — the SPA reads the token from the mailed link's
   * URL FRAGMENT (`/verify#token=…` — a fragment never rides a GET the server
   * logs) and POSTs it here. Classified, never thrown.
   */
  async verify(token: string): Promise<VerifyOutcome> {
    const res = await fetch(`/api/store/verify`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });
    if (res.ok) {
      return 'verified';
    }
    if (res.status === 400 || res.status === 404) {
      return 'invalid'; // uniform "invalid or expired" (404 = registration off)
    }
    if (res.status === 429) {
      return 'rate_limited';
    }
    return 'unavailable';
  }

  // ---- bearer: checkout + the buyer's own orders --------------------------

  /**
   * POST /api/store/checkout — ONE idempotent transaction on the server: mints
   * the order, reserves the stock, marks the listings sold. `clientOpId` is the
   * idempotency identity (the server binds it to the account + the sorted
   * cart); a replay returns the same receipt, a divergent reuse is a 409.
   */
  checkout(clientOpId: string, listingIds: string[]): Promise<CheckoutReceipt> {
    return this.api<CheckoutReceipt>('/store/checkout', {
      method: 'POST',
      body: JSON.stringify({
        client_op_id: clientOpId,
        listing_ids: listingIds,
      }),
    });
  }

  /** The authenticated buyer's own orders (account-scoped, Storefront projection). */
  myOrders(): Promise<StorefrontRecord[]> {
    return this.api<StorefrontRecord[]>('/store/orders');
  }

  /** One of the buyer's own orders (404 on a foreign/missing id — no oracle). */
  myOrder(id: string): Promise<StorefrontRecord> {
    return this.api<StorefrontRecord>(`/store/orders/${id}`);
  }
}
